import { styled } from '@linaria/react'
import { IconSC } from 'atoms/Icon'
import { PosterSC } from 'atoms/ImagePoster'
import { TextBox } from 'atoms/TextBox'
import {
  cardHoverFavoriteButtonStyles,
  cardHoverFavoriteSlotLayoutStyles,
} from 'blocks/Cards/cardHoverFavorite.styled'
import { COLOR, FIXED_INDENTS, MEDIA, SHADOW, TRANSITIONS } from 'vars/index'
import { ImageBackgroundSC, PosterContainerSC } from '../base-card'
import {
  BaseCardSC,
  LockedPosterSC,
  PosterContainerContentSC,
  TopCardBaseSC,
  TopTvSimpleCardBaseSC,
} from '../base-card/BaseCard.styled'
import { Card } from '../Card'
import { CARDS_ASPECT_RATIO, CardPosterSize } from '../Card.h'

export const TVCardFavoriteSC = styled.div<{
  viewedButtonFavorite?: boolean
  showOnHover?: boolean
}>`
  position: absolute;
  top: var(${FIXED_INDENTS.INDENT_20});
  right: var(${FIXED_INDENTS.INDENT_20});
  z-index: 102;
  display: ${({ viewedButtonFavorite }) =>
    viewedButtonFavorite ? 'flex' : 'none'};
  opacity: ${({ viewedButtonFavorite, showOnHover }) =>
    viewedButtonFavorite && showOnHover ? 1 : 0};
  pointer-events: ${({ viewedButtonFavorite, showOnHover }) =>
    viewedButtonFavorite && showOnHover ? 'auto' : 'none'};
  transition: opacity var(${TRANSITIONS.LINEAR_400});
  ${cardHoverFavoriteSlotLayoutStyles}
  ${cardHoverFavoriteButtonStyles}

  ${MEDIA.NO_TOUCH} {
    ${BaseCardSC}:hover &,
    ${TopCardBaseSC}:hover &,
    ${TopTvSimpleCardBaseSC}:hover & {
      opacity: ${({ viewedButtonFavorite }) => (viewedButtonFavorite ? 1 : 0)};
      pointer-events: ${({ viewedButtonFavorite }) =>
        viewedButtonFavorite ? 'auto' : 'none'};
    }
  }

  ${MEDIA.TOUCH} {
    display: none;
  }
`

export const TVCardContent = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  padding: var(${FIXED_INDENTS.INDENT_50});

  ${MEDIA.TOUCH} {
    display: none;
  }
`

export const TvCardChannelNumberSC = styled(TextBox)`
  position: absolute;
  bottom: var(${FIXED_INDENTS.INDENT_20});
  right: var(${FIXED_INDENTS.INDENT_40});
`

/**
 * Corner labels keep BaseCard's `z-index: 1`, under the hover state layer
 * (`PosterContainerSC::after`, `z-index: 2`) and the geoblock overlay
 * (`z-index: 3`). Nothing in `CardChildContentSC` paints over them by default:
 * the channel logo is the poster and the fallback title is
 * `PosterContainerContentSC`, both at `z-index: 1` and earlier in the DOM, while
 * the children hold only the faded-out hover title and my-list slot.
 */
export const TvSimpleCardSC = styled(Card)`
  ${MEDIA.NO_TOUCH} {
    &:hover {
      box-shadow: ${({ scaleOnHover }) =>
        scaleOnHover ? `var(${SHADOW.CARD})` : 'none'};
    }

    // FP-4299: hovered pinlock frame hides the lock circle; do not hide the
    // channel logo on unlocked cards.
    &:hover,
    ${TopTvSimpleCardBaseSC}:hover & {
      ${ImageBackgroundSC} {
        opacity: ${({ locked }) => (locked ? 0 : 1)};
      }
    }
  }

  ${PosterContainerSC} {
    ${ImageBackgroundSC} {
      width: ${() => `${CardPosterSize.TvSimplePosterContainerWidth}%`};
      height: ${() => `${CardPosterSize.TvSimplePosterContainerHeight}%`};
      left: 50%;
      top: 50%;
      transform: translateY(-50%) translateX(-50%);
      background-color: var(${COLOR.SURFACE_OPACITY_DARK_25});
      border-radius: 50%;
      border: 0.2px solid var(${COLOR.SURFACE_OPACITY_LIGHT_15});
      overflow: hidden;

      ${PosterSC} {
        width: ${() => `${CardPosterSize.TvSimplePosterWidth}%`};
        height: ${() => `${CardPosterSize.TvSimplePosterWidth}%`};
      }

      ${LockedPosterSC} {
        ${IconSC} {
          width: ${() => `${CardPosterSize.TvSimplePosterWidth}%`};
          height: ${() => `${CardPosterSize.TvSimplePosterWidth}%`};
        }
      }
    }

    ${PosterContainerContentSC} {
      padding: var(${FIXED_INDENTS.INDENT_50});
    }
  }
`

export const TopTvSimpleCardSC = styled(TopTvSimpleCardBaseSC)`
  cursor: pointer;
  position: relative;

  ${TvSimpleCardSC} {
    position: absolute;
    top: 0;
    right: 0;
    aspect-ratio: ${CARDS_ASPECT_RATIO.HORIZONTAL_DEFAULT};
    height: 100%;
    width: initial;
  }
`
